import { useState, useMemo } from "react";
import { Separator } from "@/components/ui/separator";
import { Database } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetAiPrompts, useGetPayloadTokens } from "@/resources/ai-prompt";
import { NavItem } from "./ai-configuration/NavItem";
import { PayloadLibraryView } from "./ai-configuration/PayloadLibraryView";
import { FeatureCategoryView } from "./ai-configuration/FeatureCategoryView";
import { PromptEditorPane } from "./ai-configuration/PromptEditorPane";
import { PromptValidatorChecks } from "./ai-configuration/PromptValidatorChecks";
import { featureCategories, sharedValidatorCategory, allCategories, payloadLibraryNavItem } from "./ai-configuration/constants";
import type { FeatureCategoryKey } from "./ai-configuration/types";

export function AIConfigurationTab() {
  const [activeCategoryKey, setActiveCategoryKeyRaw] = useState<FeatureCategoryKey>("ab-test");
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  // AUT-394: the prompt a "Manage in Validator →" cross-link points at, so the Validator page
  // can expand + highlight that prompt's row. Cleared on any manual category switch.
  const [focusValidatorFeature, setFocusValidatorFeature] = useState<string | null>(null);

  const { data: aiPrompts = [], isLoading } = useGetAiPrompts();
  const { data: payloadTokens = [], isLoading: isLoadingTokens } = useGetPayloadTokens();

  const isPayloadLibrary = activeCategoryKey === "payload-library";
  const activeCategory = isPayloadLibrary
    ? null
    : (allCategories.find((c) => c.key === activeCategoryKey) ?? allCategories[0]);

  const categoryPrompts = useMemo(() => {
    if (!activeCategory) return [];
    return aiPrompts.filter((p) =>
      p.feature_key.startsWith(activeCategory.promptKeyPrefix)
    );
  }, [aiPrompts, activeCategory]);

  const selectedPrompt = useMemo(() => {
    if (selectedPromptId === null || isPayloadLibrary) return null;
    return categoryPrompts.find((p) => p.id === selectedPromptId) ?? null;
  }, [categoryPrompts, selectedPromptId, isPayloadLibrary]);

  const setActiveCategoryKey = (k: FeatureCategoryKey) => {
    setActiveCategoryKeyRaw(k);
    setSelectedPromptId(null);
    setFocusValidatorFeature(null);
  };

  const selectPrompt = (id: number | null) => {
    setSelectedPromptId(id);
  };

  return (
    <div
      className={cn(
        // items-start so a short column (e.g. a filtered-to-empty Checks list) isn't stretched to
        // the tallest column's height, which left a large empty block + a dangling divider below
        // the content (AUT-398).
        "grid grid-cols-1 items-start",
        selectedPrompt
          ? "lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)]"
          : "lg:grid-cols-[180px_minmax(0,1fr)]"
      )}
    >
      <nav aria-label="AI features" className="space-y-1 lg:border-r lg:border-border lg:pr-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 pb-1">
          Features
        </p>
        {featureCategories.map((cat) => (
          <NavItem
            key={cat.key}
            category={cat}
            active={activeCategoryKey === cat.key}
            onSelect={setActiveCategoryKey}
          />
        ))}

        <div className="pt-3">
          <Separator className="mb-3" />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 pb-1">
            Shared
          </p>
          <NavItem
            category={sharedValidatorCategory}
            active={activeCategoryKey === "shared-validator"}
            onSelect={setActiveCategoryKey}
          />
        </div>

        <div className="pt-3">
          <Separator className="mb-3" />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 pb-1">
            Data
          </p>
          <NavItem
            category={payloadLibraryNavItem}
            active={activeCategoryKey === "payload-library"}
            onSelect={setActiveCategoryKey}
          />
        </div>
      </nav>

      {isPayloadLibrary ? (
        <div className="min-w-0 lg:px-6 pt-6 lg:pt-0 space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-xl font-semibold text-foreground">Payload Library</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Reference of all data tokens available to AI prompts
            </p>
          </div>
          <PayloadLibraryView tokens={payloadTokens} isLoading={isLoadingTokens} />
        </div>
      ) : (
        <>
          {activeCategory && (
            <FeatureCategoryView
              activeCategory={activeCategory}
              categoryPrompts={categoryPrompts}
              isLoading={isLoading}
              selectedPromptId={selectedPromptId}
              payloadTokens={payloadTokens}
              onSelectPrompt={selectPrompt}
              focusValidatorFeature={focusValidatorFeature}
            />
          )}

          {selectedPrompt && activeCategory && (
            <PromptEditorPane
              key={selectedPrompt.id}
              prompt={selectedPrompt}
              featureLabel={activeCategory.label}
              payloadTokens={payloadTokens}
              onClose={() => selectPrompt(null)}
              afterBody={
                <>
                  <Separator />
                  <PromptValidatorChecks
                    key={`validator-${selectedPrompt.id}`}
                    prompt={selectedPrompt}
                    onNavigateToValidator={() => {
                      setActiveCategoryKey("shared-validator");
                      setFocusValidatorFeature(selectedPrompt.feature_key);
                    }}
                  />
                </>
              }
            />
          )}
        </>
      )}
    </div>
  );
}
